import { Component, ElementRef, inject, ViewChild } from '@angular/core';

import { CommonDialogWrapperComponent } from '@dialogs/common-dialog-wrapper/common-dialog-wrapper.component';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ButtonComponent, ChipComponent } from '@mds/ui';
import {
  CommonCloseDialogType,
  CommonDialogButton
} from '@dialogs/common-dialog-wrapper/common-dialog-wrapper.model';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from '@services/toaster.service';
import { InformationRequestService } from '@pages/information-requests/information-request.service';

@Component({
  selector: 'app-bulk-action',
  standalone: true,
  imports: [
    CommonDialogWrapperComponent,
    MatDialogModule,
    ButtonComponent,
    MatIconModule,
    ChipComponent
],
  templateUrl: './bulk-action.component.html',
  styleUrls: ['./bulk-action.component.scss'],
  providers: [InformationRequestService]
})
export class BulkActionComponent {
  submitButton: CommonDialogButton = {
    text: 'Submit Responses',
    type: 'primary'
  };

  reloadButton: CommonDialogButton = {
    text: 'Reload',
    type: 'primary'
  };
  confirmButton = this.submitButton;
  cancelButton: CommonDialogButton = { text: 'Cancel', type: 'secondary' };
  protected isTemplateUploaded = false;
  fileToUpload: File | null = null;
  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef<HTMLInputElement>;

  private dialogRef = inject(MatDialogRef<BulkActionComponent>);
  private informationRequestService = inject(InformationRequestService);
  private toastrService = inject(ToastrService);

  /**
   * Downloads the Excel template from the server.
   */
  downloadTemplate(): void {
    this.informationRequestService.downloadExcelTemplate().subscribe({
      next: (res: Blob) => {
        this.downloadFile(res, 'PCF_excel_template');
      }
    });
  }

  /**
   * Downloads a file using a Blob and filename.
   * @param blob - The file content as a Blob.
   * @param filename - The name for the downloaded file.
   */
  private downloadFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;

    const sanitizedFilename = filename.trim().replace(/\s+/g, '_');
    anchor.download = `${sanitizedFilename}.xlsx`;

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  /**
   * Handles the file input change event and stores the selected file.
   * @param event - The file input change event.
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Allowed file extensions (modify as needed)
      const allowedExtensions = ['xlsx', 'xls'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      // Validate file extension
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        this.toastrService.showMessage(
          'Invalid file type. Please select a valid Excel file.',
          4000
        );
        return;
      }

      this.fileToUpload = file;
      this.fileInput.nativeElement.value = '';
    }
  }

  removeFile() {
    this.fileToUpload = null;
  }

  /**
   * Triggers the file input click event programmatically.
   */
  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Submits the selected responses file to the server.
   */
  submitResponses(): void {
    if (!this.fileToUpload) {
      this.toastrService.showMessage(
        'Please upload your responses before clicking on “Submit Responses”.',
        4000
      );
      return;
    }

    this.informationRequestService
      .uploadExcelTemplate(this.fileToUpload)
      .subscribe({
        next: () => {
          this.isTemplateUploaded = true;
          this.confirmButton = this.reloadButton;
          this.cancelButton = null;
          this.toastrService.showMessage('File Uploaded Successfully');
        }
      });
  }

  /**
   * Reloads the page after closing the dialog.
   */
  reloadPage(): void {
    this.closeDialog(true);
  }

  /**
   * Closes the dialog with an optional reload flag.
   * @param reload - Whether to reload the page after closing the dialog.
   */
  closeDialog(reload = false): void {
    this.dialogRef.close({ reload });
  }

  onDialogAction(event: CommonCloseDialogType): void {
    if (event === CommonCloseDialogType.CONFIRM) {
      if (this.isTemplateUploaded) {
        this.reloadPage();
      } else {
        this.submitResponses();
      }
    } else {
      this.closeDialog();
    }
  }
}
